import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Plus, Edit2, Save, X, Trash2, AlertTriangle, Copy, CheckCircle2, UserMinus } from 'lucide-react';
import { PageLayout } from '../../shared/PageLayout';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { ConfirmDialog } from '../../../components/shared/ConfirmDialog';
import { AddStudentPanelV2 } from '../components/AddStudentPanelV2';
import { useAppStore } from '../../../app/hooks/useAppStore';
import { getAvatarGradient } from '../utils/avatarUtils';
import { type Classroom, type TeacherAccount, type ClassroomValidationWarning, generateClassroomId, validateNewClassroom } from '../../../accounts';
import { StudentData } from './StudentDetailPanel';
interface ManagementPageProps {
  activeAccount: TeacherAccount;
  classrooms?: Classroom[];
  selectedClassroomId?: string | null;
  students: StudentData[];
}
export function ManagementPage({
  activeAccount,
  classrooms = [],
  selectedClassroomId = null,
  students
}: ManagementPageProps) {
  const {
    accounts,
    addClassroom,
    updateClassroom,
    removeClassroom,
    removeLearnerFromClassroom,
    unlinkTeacherFromLearner
  } = useAppStore();
  const [isEditingClass, setIsEditingClass] = useState<string | null>(null);
  const [editClassName, setEditClassName] = useState('');
  const [editGroupLevel, setEditGroupLevel] = useState(6);
  const [isAddingClass, setIsAddingClass] = useState(false);
  const [newClassName, setNewClassName] = useState('');
  const [newGroupLevel, setNewGroupLevel] = useState(6);
  const [classWarnings, setClassWarnings] = useState<ClassroomValidationWarning[]>([]);
  const [confirmDuplicate, setConfirmDuplicate] = useState(false);
  const [deleteClassroomId, setDeleteClassroomId] = useState<string | null>(null);
  const [showAddStudent, setShowAddStudent] = useState(false);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [pendingRemoveStudent, setPendingRemoveStudent] = useState<{
    studentId: string;
    studentName: string;
    classroomId: string;
    classroomName: string;
  } | null>(null);
  const [pendingUnlinkStudent, setPendingUnlinkStudent] = useState<{
    studentId: string;
    studentName: string;
  } | null>(null);
  const handleSaveClass = (classroomId: string) => {
    if (editClassName.trim()) {
      updateClassroom(activeAccount.id, classroomId, {
        name: editClassName.trim(),
        groupLevel: editGroupLevel
      });
      setIsEditingClass(null);
    }
  };
  const handleAddClassroom = () => {
    if (!newClassName.trim()) return;
    const warnings = validateNewClassroom(accounts, newClassName.trim(), activeAccount.id);
    const hasSameTeacherDuplicate = warnings.some(w => w.details.teacherId === activeAccount.id);
    if (hasSameTeacherDuplicate) {
      setClassWarnings(warnings);
      return;
    }
    if (warnings.length > 0 && !confirmDuplicate) {
      setClassWarnings(warnings);
      return;
    }
    const newClassroom: Classroom = {
      id: generateClassroomId(),
      name: newClassName.trim(),
      groupLevel: newGroupLevel,
      learnerIds: [],
      createdAt: Date.now(),
      teacherId: activeAccount.id,
      joinCode: '' // Auto-generated by addClassroom()
    };
    addClassroom(activeAccount.id, newClassroom);
    setIsAddingClass(false);
    setNewClassName('');
    setNewGroupLevel(6);
    setClassWarnings([]);
    setConfirmDuplicate(false);
  };
  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };
  const activeClassroom = selectedClassroomId ? classrooms.find(c => c.id === selectedClassroomId) : null;
  const classStudents = activeClassroom ? students.filter(s => activeClassroom.learnerIds.includes(s.id)) : students;
  return <PageLayout title="Klas Beheer" description="Beheer je klassen en leerlingen" data-id="element-5626">
      <div className="max-w-5xl mx-auto space-y-6 pb-12" data-id="element-5627">
        {/* Classrooms Section */}
        <Card className="border-slate-200 shadow-sm bg-white overflow-hidden" data-id="element-5628">
          <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-100" data-id="element-5629">
            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-5630">
              Mijn Klassen
            </h3>
            <Button onClick={() => setIsAddingClass(true)} variant="primary" size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-[0_4px_0_0_rgb(67,56,202)] hover:shadow-[0_4px_0_0_rgb(67,56,202)]" data-id="element-5631">
              <Plus className="w-4 h-4 mr-2" data-id="element-5632" />
              Klas toevoegen
            </Button>
          </div>
          <div className="p-4 space-y-4" data-id="element-5633">
            {isAddingClass && <div className="p-5 bg-indigo-50/50 border border-indigo-100 rounded-xl" data-id="element-5634">
                <h4 className="text-sm font-bold text-slate-900 mb-3" data-id="element-5635">
                  Nieuwe klas toevoegen
                </h4>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3" data-id="element-5636">
                  <div className="flex-1 w-full" data-id="element-5637">
                    <Input variant="admin" label="Klasnaam" value={newClassName} onChange={e => setNewClassName(e.target.value)} placeholder="Bijv. Groep 7B" autoFocus data-id="element-5638" />
                  </div>
                  <div className="w-full sm:w-32" data-id="element-5639">
                    <Select variant="admin" label="Niveau" value={newGroupLevel} onChange={e => setNewGroupLevel(parseInt(e.target.value))} options={[{
                  value: '6',
                  label: 'Groep 6'
                }, {
                  value: '7',
                  label: 'Groep 7'
                }, {
                  value: '8',
                  label: 'Groep 8'
                }]} data-id="element-5640" />
                  </div>
                  <div className="flex items-center gap-2 self-end sm:self-auto mt-2 sm:mt-5" data-id="element-5641">
                    <Button onClick={handleAddClassroom} variant="primary" disabled={!newClassName.trim()} className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-[0_4px_0_0_rgb(67,56,202)] hover:shadow-[0_4px_0_0_rgb(67,56,202)]" data-id="element-5642">
                      {confirmDuplicate ? 'Toch toevoegen' : 'Toevoegen'}
                    </Button>
                    <Button onClick={() => {
                  setIsAddingClass(false);
                  setClassWarnings([]);
                  setConfirmDuplicate(false);
                }} variant="secondary" data-id="element-5643">
                      Annuleren
                    </Button>
                  </div>
                </div>
                {classWarnings.length > 0 && <div className="mt-3 space-y-2" data-id="element-5644">
                    {classWarnings.map((w, i) => <div key={i} className={`flex items-start gap-2 p-3 rounded-lg text-sm ${w.details.teacherId === activeAccount.id ? 'bg-red-50 border border-red-200 text-red-800' : 'bg-amber-50 border border-amber-200 text-amber-800'}`} data-id="element-5645">
                        <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-5646" />
                        <div data-id="element-5647">
                          <p className="font-medium" data-id="element-5648">{w.message}</p>
                          {w.details.teacherId !== activeAccount.id && !confirmDuplicate && <button onClick={() => setConfirmDuplicate(true)} className="mt-1 text-xs font-bold underline hover:no-underline" data-id="element-5649">
                                Ja, toch aanmaken
                              </button>}
                        </div>
                      </div>)}
                  </div>}
              </div>}

            {classrooms.length === 0 && !isAddingClass ? <div className="text-center py-10 bg-slate-50 rounded-xl border border-slate-200 border-dashed" data-id="element-5650">
                <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 mx-auto mb-3" data-id="element-5651">
                  <Users className="w-6 h-6" data-id="element-5652" />
                </div>
                <p className="text-slate-500 mb-4 font-medium" data-id="element-5653">
                  Je hebt nog geen klassen aangemaakt.
                </p>
                <Button onClick={() => setIsAddingClass(true)} variant="outline" className="border-indigo-200 text-indigo-600 hover:bg-indigo-50 hover:border-indigo-300" data-id="element-5654">
                  Maak je eerste klas aan
                </Button>
              </div> : <div className="grid grid-cols-1 gap-3" data-id="element-5655">
                {classrooms.map((classroom: Classroom) => <div key={classroom.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-white border border-slate-200 rounded-xl hover:border-indigo-200 transition-colors shadow-sm" data-id="element-5656">
                    {isEditingClass === classroom.id ? <div className="flex-1 flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full" data-id="element-5657">
                        <div className="flex-1 w-full" data-id="element-5658">
                          <Input variant="admin" value={editClassName} onChange={e => setEditClassName(e.target.value)} placeholder="Klasnaam" autoFocus data-id="element-5659" />
                        </div>
                        <div className="w-full sm:w-32" data-id="element-5660">
                          <Select variant="admin" value={editGroupLevel} onChange={e => setEditGroupLevel(parseInt(e.target.value))} options={[{
                    value: '6',
                    label: 'Groep 6'
                  }, {
                    value: '7',
                    label: 'Groep 7'
                  }, {
                    value: '8',
                    label: 'Groep 8'
                  }]} data-id="element-5661" />
                        </div>
                        <div className="flex items-center gap-2 self-end sm:self-auto mt-2 sm:mt-0" data-id="element-5662">
                          <Button onClick={() => handleSaveClass(classroom.id)} variant="primary" size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-[0_4px_0_0_rgb(67,56,202)] hover:shadow-[0_4px_0_0_rgb(67,56,202)]" data-id="element-5663">
                            <Save className="w-4 h-4" data-id="element-5664" />
                          </Button>
                          <Button onClick={() => setIsEditingClass(null)} variant="secondary" size="sm" data-id="element-5665">
                            <X className="w-4 h-4" data-id="element-5666" />
                          </Button>
                        </div>
                      </div> : <>
                        <div className="flex items-center gap-4 mb-3 sm:mb-0" data-id="element-5667">
                          <div className="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-600 font-black text-lg border border-indigo-100" data-id="element-5668">
                            {classroom.groupLevel}
                          </div>
                          <div data-id="element-5669">
                            <h4 className="font-bold text-slate-900 text-base" data-id="element-5670">
                              {classroom.name}
                            </h4>
                            <div className="flex items-center gap-3 text-sm text-slate-500 font-medium mt-0.5" data-id="element-5671">
                              <span data-id="element-5672">
                                {classroom.learnerIds.length} leerlingen
                              </span>
                              <span className="w-1 h-1 rounded-full bg-slate-300" data-id="element-5673" />
                              <div className="flex items-center gap-1.5" data-id="element-5674">
                                <span data-id="element-5675">Code:</span>
                                <span className="font-mono font-bold text-slate-700 bg-slate-100 px-1.5 py-0.5 rounded" data-id="element-5676">
                                  {classroom.joinCode}
                                </span>
                                <button onClick={() => handleCopyCode(classroom.joinCode)} className="text-slate-400 hover:text-indigo-600 transition-colors" title="Kopieer code" data-id="element-5677">
                                  {copiedCode === classroom.joinCode ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" data-id="element-5678" /> : <Copy className="w-3.5 h-3.5" data-id="element-5679" />}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2" data-id="element-5680">
                          <Button onClick={() => {
                    setIsEditingClass(classroom.id);
                    setEditClassName(classroom.name);
                    setEditGroupLevel(classroom.groupLevel);
                  }} variant="ghost" size="sm" data-id="element-5681">
                            <Edit2 className="w-4 h-4 mr-2" data-id="element-5682" /> Bewerk
                          </Button>
                          <Button onClick={() => setDeleteClassroomId(classroom.id)} variant="ghost" size="sm" className="text-rose-600 hover:bg-rose-50 hover:text-rose-700" data-id="element-5683">
                            <Trash2 className="w-4 h-4" data-id="element-5684" />
                          </Button>
                        </div>
                      </>}
                  </div>)}
              </div>}
          </div>
        </Card>

        {/* Students Management Section */}
        {classrooms.length > 0 && <Card className="border-slate-200 shadow-sm bg-white overflow-hidden" data-id="element-5685">
            <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-100" data-id="element-5686">
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-5687">
                Leerlingen Beheer
              </h3>
              <Button onClick={() => setShowAddStudent(true)} variant="primary" size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-[0_4px_0_0_rgb(67,56,202)] hover:shadow-[0_4px_0_0_rgb(67,56,202)]" data-id="element-5688">
                <Plus className="w-4 h-4 mr-2" data-id="element-5689" />
                Leerling toevoegen
              </Button>
            </div>
            <div className="p-4" data-id="element-5690">
              {classStudents.length === 0 ? <div className="text-center py-8 bg-slate-50 rounded-xl border border-slate-200 border-dashed" data-id="element-5691">
                  <p className="text-slate-500 font-medium" data-id="element-5692">
                    Geen leerlingen in deze weergave.
                  </p>
                </div> : <div className="divide-y divide-slate-100 border border-slate-200 rounded-xl overflow-hidden bg-white" data-id="element-5693">
                  <div className="flex items-center justify-between p-4 bg-slate-50 text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-5694">
                    <div data-id="element-5695">Leerling</div>
                    <div data-id="element-5696">Acties</div>
                  </div>
                  {classStudents.map((student, i) => {
              const initial = student.name.charAt(0).toUpperCase();
              const gradient = getAvatarGradient(student.name);
              return <div key={student.id} className={`flex items-center justify-between px-4 py-2.5 hover:bg-slate-50 transition-colors ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}`} data-id="element-5697">
                        <div className="flex items-center gap-3" data-id="element-5698">
                          <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${gradient} text-white flex items-center justify-center font-bold text-sm shadow-sm`} data-id="element-5699">
                            {initial}
                          </div>
                          <div className="font-bold text-sm text-slate-900" data-id="element-5700">
                            {student.name}
                          </div>
                        </div>
                        <div className="flex items-center gap-1" data-id="element-5701">
                          {activeClassroom && <button onClick={() => {
                    setPendingRemoveStudent({
                      studentId: student.id,
                      studentName: student.name,
                      classroomId: activeClassroom.id,
                      classroomName: activeClassroom.name
                    });
                  }} className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors" title="Verwijder uit klas" data-id="element-5702">
                              <X className="w-4 h-4" data-id="element-5703" />
                            </button>}
                          <button onClick={() => {
                    setPendingUnlinkStudent({
                      studentId: student.id,
                      studentName: student.name
                    });
                  }} className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors" title="Ontkoppelen" data-id="element-5704">
                            <UserMinus className="w-4 h-4" data-id="element-5705" />
                          </button>
                        </div>
                      </div>;
            })}
                </div>}
            </div>
          </Card>}
      </div>

      <AnimatePresence data-id="element-5706">
        {showAddStudent && classrooms.length > 0 && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-5707">
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={() => setShowAddStudent(false)} data-id="element-5708" />
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} className="relative w-full max-w-2xl bg-white rounded-2xl shadow-xl overflow-hidden z-10 max-h-[90vh] flex flex-col" data-id="element-5709">
              <div className="flex items-center justify-between p-4 border-b border-slate-100" data-id="element-5710">
                <h2 className="text-lg font-bold text-slate-900" data-id="element-5711">
                  Leerling toevoegen
                </h2>
                <button onClick={() => setShowAddStudent(false)} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-5712">
                  <X className="w-5 h-5" data-id="element-5713" />
                </button>
              </div>
              <div className="p-6 overflow-y-auto" data-id="element-5714">
                <AddStudentPanelV2 activeAccount={activeAccount} onComplete={() => setShowAddStudent(false)} onCancel={() => setShowAddStudent(false)} classrooms={classrooms} selectedClassroomId={selectedClassroomId} data-id="element-5715" />
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      <ConfirmDialog open={!!deleteClassroomId} title="Klas verwijderen" message={`Weet je zeker dat je deze klas wilt verwijderen? De leerlingen worden niet verwijderd, alleen de klas wordt opgeheven. Je kunt dit niet ongedaan maken.`} confirmLabel="Ja, verwijder klas" cancelLabel="Annuleren" variant="danger" onConfirm={() => {
      if (deleteClassroomId) {
        removeClassroom(activeAccount.id, deleteClassroomId);
        setDeleteClassroomId(null);
      }
    }} onCancel={() => setDeleteClassroomId(null)} data-id="element-5716" />

      <ConfirmDialog open={!!pendingRemoveStudent} title="Leerling verwijderen uit klas" message={pendingRemoveStudent ? `Weet je zeker dat je ${pendingRemoveStudent.studentName} uit ${pendingRemoveStudent.classroomName} wilt verwijderen?` : ''} confirmLabel="Ja, verwijder" cancelLabel="Annuleren" variant="danger" onConfirm={() => {
      if (pendingRemoveStudent) {
        removeLearnerFromClassroom(activeAccount.id, pendingRemoveStudent.classroomId, pendingRemoveStudent.studentId);
        setPendingRemoveStudent(null);
      }
    }} onCancel={() => setPendingRemoveStudent(null)} data-id="element-5717" />

      <ConfirmDialog open={!!pendingUnlinkStudent} title="Koppeling verbreken" message={pendingUnlinkStudent ? `Weet je zeker dat je de koppeling met ${pendingUnlinkStudent.studentName} wilt verbreken?` : ''} confirmLabel="Ja, ontkoppel" cancelLabel="Annuleren" variant="danger" onConfirm={() => {
      if (pendingUnlinkStudent) {
        unlinkTeacherFromLearner(activeAccount.id, pendingUnlinkStudent.studentId);
        setPendingUnlinkStudent(null);
      }
    }} onCancel={() => setPendingUnlinkStudent(null)} data-id="element-5718" />
    </PageLayout>;
}